import { useCallback, useMemo } from 'react';
import { Dictionary } from '@reduxjs/toolkit';

import { Resp, useApi } from '#/api/fetch-api';
import { OpenPosition } from '#/api/positions';
import {
  createOrder,
  CreateOrderReq,
  CreateOrderResp,
  Order,
} from '#/api/orders';
import { PerpetualMarket } from '#/api/markets';

import { signOrderRequest } from '#/features/order-signature';
import { useOpenPositionsView } from '#/features/perpetuals/open-positions-context';
import * as PositionFns from '#/features/perpetuals/trade-overview/position-functions';
import {
  usePerpetualMarketsState,
  usePerpetualMarketsStoreView,
} from '#/features/perpetuals/market-info/markets-context';
import { publishNotification } from '#/features/notifications/topic';
import { useSystemConfig } from '#/features/system/system-config-context';

import { Result } from '#/utils/types';

type ClosePositionView =
  | {
      readonly status: 'busy';
    }
  | {
      readonly status: 'idle';
      readonly orderSide: Order['side'];
      readonly orderSize: string;
      readonly helperText: string;
    };

interface ClosePosition {
  readonly actions: {
    readonly submit: (cb?: (result: Result<CreateOrderResp>) => void) => void;
  };
  readonly view: ClosePositionView;
}

export function useClosePosition(id: OpenPosition['id']): ClosePosition {
  const [{ isLoading }, exec] = useApi(createOrder);

  const { markets } = usePerpetualMarketsState();
  const { getPosition } = useOpenPositionsView();
  const handleNotifications = useHandleNotifications();
  const config = useSystemConfig();

  const position = getPosition(id);
  if (position == null) {
    throw new Error(`Position ${id} does not exist`);
  }

  const order = PositionFns.closingMarketOrderParams(position);

  const submit = useCallback(
    (cb?: (result: Result<CreateOrderResp>) => void) => {
      if (position.size.eq(0)) {
        throw new Error('Position size is already 0');
      }

      exec(prepareRequest(position, config.starknet.chainId), (resp) => {
        if (resp.ok) {
          handleNotifications(resp.data);
        }
        cb?.(prepareResponse(resp));
      });
    },
    [config.starknet.chainId, exec, handleNotifications, position],
  );

  return useMemo(() => {
    const actions = { submit };
    const view = createView(order, markets.entities, position, isLoading);
    return { actions, view };
  }, [submit, order, markets.entities, position, isLoading]);
}

function createView(
  order: Pick<Order, 'size' | 'side'>,
  markets: Dictionary<PerpetualMarket>,
  position: OpenPosition,
  isLoading: boolean,
) {
  if (isLoading) {
    return { status: 'busy' as const };
  }

  const market = markets[position.market];
  if (market == null) {
    return { status: 'busy' as const };
  }

  const orderSize = orderSizeView(order, market);

  return {
    status: 'idle' as const,
    orderSide: order.side,
    orderSize,
    helperText: `${orderSize} will be closed at Market price`,
  };
}

function orderSizeView(
  order: Pick<Order, 'size'>,
  market: Pick<PerpetualMarket, 'base_currency'>,
) {
  return `${order.size.toFormat()} ${market.base_currency}`;
}

function prepareRequest(
  position: OpenPosition,
  starknetChainId: string,
): CreateOrderReq {
  const order = PositionFns.closingMarketOrderParams(position);
  const request: Omit<CreateOrderReq, 'signature'> = {
    signature_timestamp: Date.now(),
    market: order.market,
    side: order.side,
    type: 'MARKET',
    instruction: 'GTC',
    size: order.size.toString(),
    price: '',
  };
  return signOrderRequest(request, starknetChainId);
}

function prepareResponse(resp: Resp<CreateOrderResp>): Result<CreateOrderResp> {
  return resp.ok
    ? { ok: true, data: resp.data }
    : { ok: false, reason: resp.message, error: resp.error };
}

function useHandleNotifications() {
  const { getMarket } = usePerpetualMarketsStoreView();

  return useCallback(
    (order: Order) => {
      const market = getMarket(order.market);
      if (market == null) return;

      publishNotification({
        kind: 'submitted-order',
        props: { order, market },
      });
    },
    [getMarket],
  );
}
